// === HoveredField.jsx  (May-01 – drag friendly / sidebar opens only via ✏️) ===
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DragIndicator }         from '@mui/icons-material';
import HoveredFieldMenu          from './HoveredFieldMenu';
import { useFieldEdit }          from '../../../contexts/FieldEditContext';
import { useFormLayout }         from '../../../contexts/FormLayoutContext';
import { DND_DEBUG } from './constants';


import { useFormContext }        from '../../../contexts/FormContext';
import { useAuth }               from '../../../contexts/AuthContext';
import { db }                    from '../../../firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { safeGetItem }           from '../../../utils/storageUtils';

/* ★ AUF-only delete guard dialog (kept in AssetManager domain) */
import AufFieldDeleteGuardDialog from '../../../AssetManager/AufFieldDeleteGuardDialog';

/* -------------------------------------------------------------------------- */
/*                               Color Palette                                */
/* -------------------------------------------------------------------------- */
const SELECT_COLOR = '#6BA6E6';          // Bright blue when selected
const HOVER_COLOR  = '#8BB7E9';          // Softer blue on hover

/* -------------------------------------------------------------------------- */
/*                          Error Boundary (unchanged)                        */
/* -------------------------------------------------------------------------- */
class ErrorBoundary extends React.Component {
  constructor (props) { super(props); this.state = { hasError:false, error:null, errorInfo:null }; }
  static getDerivedStateFromError (error) { return { hasError:true, error }; }
  componentDidCatch (error, info) {
    console.error('[HoveredField - ErrorBoundary] Caught error:', error, info);
    console.log   ('Error Component Stack:', info.componentStack);
    this.setState({ errorInfo:info });
  }
  render () {
    if (this.state.hasError) {
      return (
        <div style={{ color:'red' }}>
          <strong>Error in HoveredField:</strong>{' '}
          {this.state.error && this.state.error.toString()}
        </div>
      );
    }
    return this.props.children;
  }
}

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */
const HoveredField = React.memo(
({
  children,
  fieldId,
  isDragging = false,           // passed from FieldRenderer / DraggableItem
}) => {
  /* -------------------------------------------------------------------- */
  /*                            State & Refs                               */
  /* -------------------------------------------------------------------- */
  const [isHovered, setIsHovered] = useState(false);
  const fieldRef = useRef(null);

  const { selectedFieldForEdit } = useFieldEdit();      // *read-only* – edit via ✏️
const { deleteField, setShowDropZones, activeDragId } = useFormLayout();

  /*  we need AUF + org details for the post-delete sync */
  const { formId, formDetails } = useFormContext();
  const { organizationId } = useAuth();

  /* ★ AUF-only delete guard dialog state */
  const [aufDeleteGuardOpen, setAufDeleteGuardOpen] = useState(false);
  const [aufDeleteGuardBusy, setAufDeleteGuardBusy] = useState(false);
  const [aufDeleteGuardPayload, setAufDeleteGuardPayload] = useState(null);

  /* -------------------------------------------------------------------- */
  /*                        Derived display booleans                       */
  /* -------------------------------------------------------------------- */
  const isSelected  = useMemo(
    () => selectedFieldForEdit && selectedFieldForEdit.id === fieldId,
    [selectedFieldForEdit, fieldId],
  );
  const isHoverOnly = isHovered && !isSelected;

  /* -------------------------------------------------------------------- */
  /*   When drag becomes active, collapse hover visuals (no mousedown setState) */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    if (isDragging) {
      setIsHovered(false);
    }
  }, [isDragging]);

  /* -------------------------------------------------------------------- */
  /*    Global mouse-up  ➜  always lower the flag & collapse local hover   */
  /* -------------------------------------------------------------------- */
useEffect(() => {
  const handleUp = () => {
    // Always collapse hover visuals on mouseup
    setIsHovered(false);

    // IMPORTANT:
    // Drop zone lifecycle must be controlled ONLY by React-DnD begin/end.
    // Do not mutate showDropZones here — stale isDragging renders can
    // disarm drop targets mid-drag and cause dropped=false.
    if (DND_DEBUG) {
      console.log('[HoveredField] mouseup (no dropzone toggle)', {
        fieldId,
        activeDragId,
        isDragging,
      });
    }
  };

  document.addEventListener('mouseup', handleUp);
  return () => document.removeEventListener('mouseup', handleUp);
}, [setIsHovered, isDragging, fieldId, activeDragId]);


  /* -------------------------------------------------------------------- */
  /*         Collapse local hover when the click happens outside           */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    const outside = (e) => {
      if (fieldRef.current && !fieldRef.current.contains(e.target)) {
        setIsHovered(false);
      }
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  /* -------------------------------------------------------------------- */
  /*                              Handlers                                 */
  /* -------------------------------------------------------------------- */
  const handleDelete = async () => {
    if (!fieldId) {
      console.warn('[HoveredField] handleDelete: Invalid fieldId, skipping delete.');
      return;
    }

    // Guard: if the AUF delete dialog is already working, ignore re-entry
    if (aufDeleteGuardBusy) {
      console.warn('[HoveredField] handleDelete: ignored (AUF delete guard is busy).');
      return;
    }


    try {
      const isAUF = formDetails?.isAssetUploadForm === true;
      const fieldsMetaKey = `form_${formId}_fieldsMetadata`;

      // 🔎 Capture UFI before we mutate metadata/layout so we can prune org promotion keys if needed
      const metaSnapshot = safeGetItem(fieldsMetaKey) || {};
      const deletedUfi   = metaSnapshot?.[fieldId]?.ufi || null;
      const deletedMeta  = metaSnapshot?.[fieldId] || null;
      const deletedType  = deletedMeta?.type || null;

      console.log('[HoveredField] handleDelete → starting', {
        fieldId,
        formId,
        isAUF,
        organizationId,
        hasFieldsMetaInStorage: !!metaSnapshot && Object.keys(metaSnapshot).length > 0,
        deletedUfi,
      });
      // ─────────────────────────────────────────────────────────────
      // AUF-only: show guard dialog FIRST (does not affect other forms)
      // - We keep your existing hard-delete behavior behind choices.
      // - "Archive" and "Delete anyway" both run the same hard delete
      //   for now; "Replace / Map" is a no-op placeholder hook.
      // ─────────────────────────────────────────────────────────────
      if (isAUF) {
        const reasons = {
          // promoted: we can’t reliably know here without an index or a live org snapshot;
          // we’ll keep this false in v1 unless you choose to pass it in later.
          // (You already prune promotion config after delete below.)
          promoted: false,
          role:
            deletedMeta?.assetFieldRole === 'assetType' ||
            deletedMeta?.semanticKey === 'asset.type' ||
            Boolean(deletedMeta?.assetFieldRole) ||
            Boolean(deletedMeta?.semanticKey),
          dropdown:
            deletedType === 'single-select' ||
            deletedType === 'multi-select',
        };

        const isPublished =
          typeof formDetails?.status === 'string'
            ? (formDetails.status === 'published')
            : null;

        console.log('[HoveredField] handleDelete(AUF): opening delete guard dialog', {
          fieldId,
          deletedUfi,
          fieldLabel: deletedMeta?.label || '(unknown)',
          fieldType: deletedType,
          reasons,
          isPublished,
        });

        setAufDeleteGuardPayload({
          fieldId,
          deletedUfi,
          fieldLabel: deletedMeta?.label || deletedMeta?.name || '—',
          fieldType: deletedType || '—',
          fieldUfi: deletedUfi || null,
          reasons,
          isPublished,
        });
        setAufDeleteGuardOpen(true);
        return; // stop here; user decides in dialog
      }

      // 1) Run your existing builder delete (updates local layout + metadata + storage)
      await deleteField(fieldId);

      // 2) NOTE:
      // AUF delete now returns early above (opens AufFieldDeleteGuardDialog),
      // so this AUF-only block is only reachable if you remove that early return.
      // We keep the logic here for parity/reference, but v1 execution happens
      // inside handleAufDeleteGuardAction().

      if (isAUF) {
        console.warn(
          '[HoveredField] handleDelete(AUF): unreachable path (dialog intercepts AUF deletes). ' +
          'If you see this, AUF intercept was bypassed.'
        );

        if (!organizationId) {
          console.warn('[HoveredField] handleDelete(AUF): organizationId missing – skipping org sync.');
        } else if (!deletedUfi) {
          console.log('[HoveredField] handleDelete(AUF): No UFI found for field → nothing to prune in org promotion keys.');
        } else {
          try {
            const orgRef  = doc(db, 'organizations', organizationId);
            const orgSnap = await getDoc(orgRef);

            if (!orgSnap.exists()) {
              console.warn('[HoveredField] handleDelete(AUF): organizations doc not found, skipping org sync.');
            } else {
              const org = orgSnap.data() || {};
              const cfg = org.assetPromotionConfig || {};
              const before = {
                keys     : Array.isArray(cfg.keys) ? cfg.keys.slice() : [],
                arrayKeys: Array.isArray(cfg.arrayKeys) ? cfg.arrayKeys.slice() : [],
                dateKeys : Array.isArray(cfg.dateKeys) ? cfg.dateKeys.slice() : [],
              };

              const prune = (arr) => Array.isArray(arr) ? arr.filter((k) => k !== deletedUfi) : [];

              const nextCfg = {
                keys     : prune(before.keys),
                arrayKeys: prune(before.arrayKeys),
                dateKeys : prune(before.dateKeys),
              };

              const changed =
                before.keys.length     !== nextCfg.keys.length ||
                before.arrayKeys.length!== nextCfg.arrayKeys.length ||
                before.dateKeys.length !== nextCfg.dateKeys.length;

              console.log('[HoveredField] handleDelete(AUF): pruning promotion keys', {
                deletedUfi,
                before,
                after: nextCfg,
                changed,
              });

              if (changed) {
                await updateDoc(orgRef, { assetPromotionConfig: nextCfg });
                console.log('[HoveredField] handleDelete(AUF): assetPromotionConfig updated successfully.');
              } else {
                console.log('[HoveredField] handleDelete(AUF): assetPromotionConfig unchanged (UFI was not promoted).');
              }
            }
          } catch (orgErr) {
            console.warn('[HoveredField] handleDelete(AUF): org sync failed (non-fatal):', orgErr?.message || orgErr);
          }
        }

        // NOTE: We intentionally do NOT rebuild the catalog here.
        // Your publish flow writes the canonical blobs (fieldsMetadata/layoutConfig) and then
        // calls buildAssetFilterCatalogHttp, ensuring labels/choices reflect the current AUF.
        console.log('[HoveredField] handleDelete(AUF): Skipping catalog rebuild now; publish will refresh catalog blobs.');
      }

      console.log('[HoveredField] handleDelete: completed', { fieldId, formId });
    } catch (err) {
      console.error('[HoveredField] handleDelete: ERROR', err?.message || err);
    }
  };

  /* -------------------------------------------------------------------- */
  /*  AUF delete guard dialog handler (Archive / Replace / Delete)         */
  /* -------------------------------------------------------------------- */
  const handleAufDeleteGuardAction = async (action) => {
    try {
      console.log('[HoveredField] AUF delete guard action:', action);

      if (action === 'replace') {
        // Placeholder hook for v2 mapping UI
        // (Do NOT delete anything yet.)
        console.log(
          '[HoveredField] AUF delete guard: Replace/Map chosen (no-op in v1).'
        );
        setAufDeleteGuardOpen(false);
        setAufDeleteGuardPayload(null);
        return;
      }

      if (action !== 'archive' && action !== 'delete') {
        // Unknown action → treat as cancel
        setAufDeleteGuardOpen(false);
        setAufDeleteGuardPayload(null);
        return;
      }

      setAufDeleteGuardBusy(true);

      const payload       = aufDeleteGuardPayload || {};
      const targetFieldId = payload.fieldId || fieldId;
      const isAUF         = formDetails?.isAssetUploadForm === true;
      const fieldsMetaKey = `form_${formId}_fieldsMetadata`;
      const metaSnapshot  = safeGetItem(fieldsMetaKey) || {};
      const deletedMeta   = metaSnapshot?.[targetFieldId] || null;
      const deletedUfi =
        metaSnapshot?.[targetFieldId]?.ufi || payload.fieldUfi || null;

      console.log(
        '[HoveredField] AUF delete guard: proceeding with hard delete',
        {
          action,
          targetFieldId,
          deletedUfi,
        }
      );

      /* --------------------------------------------------------------
       * ① Archive path – capture the field before we delete it
       *    and append it to forms/{formId}.archivedAufFields
       *    (includes a precise wasLibraryFilter flag based on
       *     organizations.assetPromotionConfig.{keys,arrayKeys,dateKeys})
       * ------------------------------------------------------------ */
      if (action === 'archive' && isAUF && deletedMeta && formId) {
        try {
          const formRef = doc(db, 'forms', String(formId));

          const archiveUfi =
            deletedMeta.ufi || payload.fieldUfi || null;

          // Default: assume it was NOT a library filter
          let wasLibraryFilter = false;

          if (organizationId && archiveUfi) {
            try {
              const orgRef  = doc(db, 'organizations', organizationId);
              const orgSnap = await getDoc(orgRef);

              if (orgSnap.exists()) {
                const org = orgSnap.data() || {};
                const cfg = org.assetPromotionConfig || {};

                const keys     = Array.isArray(cfg.keys)
                  ? cfg.keys
                  : [];
                const arrayKeys = Array.isArray(cfg.arrayKeys)
                  ? cfg.arrayKeys
                  : [];
                const dateKeys  = Array.isArray(cfg.dateKeys)
                  ? cfg.dateKeys
                  : [];

                wasLibraryFilter =
                  keys.includes(archiveUfi) ||
                  arrayKeys.includes(archiveUfi) ||
                  dateKeys.includes(archiveUfi);
              } else {
                console.warn(
                  '[HoveredField] AUF archive: organizations doc not ' +
                    'found while computing wasLibraryFilter.'
                );
              }
            } catch (flagErr) {
              console.warn(
                '[HoveredField] AUF archive: failed to compute ' +
                  'wasLibraryFilter (non-fatal):',
                flagErr?.message || flagErr
              );
            }
          }

          // ───────────────────────────────────────────────────────────
          // AUF RESTORE SUPPORT (preserve UFI + optionIds)
          // Snapshot dropdown option set so restore can preserve optionIds
          // even if dropdownOptions is later pruned/rewritten.
          // Guardrail: store organizationId to prevent cross-org restore.
          // ───────────────────────────────────────────────────────────
          let optionsSnapshot = null;

          try {
            const isDropdown =
              deletedMeta?.type === 'single-select' ||
              deletedMeta?.type === 'multi-select';

            const setUfi = deletedMeta?.optionsSetUFI || null;

            // Make this block self-contained so it never depends on outer scope.
            // (Prevents ReferenceError if someone reorders code later.)
            const ufiForSnapshotLog = deletedMeta?.ufi || payload.fieldUfi || null;

            if (isDropdown && setUfi) {
              const allOpts = safeGetItem(`form_${formId}_dropdownOptions`) || {};
              const arr = Array.isArray(allOpts?.[setUfi]) ? allOpts[setUfi] : null;

              if (arr && arr.length > 0) {
                optionsSnapshot = arr;
              }

              console.log('[HoveredField] AUF archive: options snapshot', {
                fieldId: targetFieldId,
                ufi: ufiForSnapshotLog,
                optionsSetUFI: setUfi,
                count: Array.isArray(optionsSnapshot) ? optionsSnapshot.length : 0,
              });
            }
          } catch (optSnapErr) {
            console.warn(
              '[HoveredField] AUF archive: failed to snapshot dropdownOptions (non-fatal):',
              optSnapErr?.message || optSnapErr
            );
            optionsSnapshot = null;
          }


          const archiveEntry = {
            organizationId: organizationId || null, // ✅ guardrail: org-scoped restore
            ufi           : archiveUfi,
            label         : deletedMeta.label || deletedMeta.name || null,
            type          : deletedMeta.type || null,
            formId        : formId || null,
            assetFieldRole: deletedMeta.assetFieldRole || null,
            semanticKey   : deletedMeta.semanticKey || null,
            optionsSetUFI : deletedMeta.optionsSetUFI || null,
            optionsSnapshot, // ✅ restore-preserving dropdown optionIds
            archivedAt    : new Date().toISOString(),
            wasLibraryFilter,
          };


          console.log(
            '[HoveredField] AUF archive: appending forms.archivedAufFields entry:',
            archiveEntry
          );

          await updateDoc(formRef, {
            archivedAufFields: arrayUnion(archiveEntry),
          });
        } catch (archErr) {
          console.warn(
            '[HoveredField] AUF archive: failed to append ' +
              'forms.archivedAufFields (non-fatal):',
            archErr?.message || archErr
          );
        }
      }


      /* --------------------------------------------------------------
       * ② Builder delete – shared by Archive + Delete in v1
       *    (removes field from layout, metadata, and storage)
       * ------------------------------------------------------------ */
      await deleteField(targetFieldId);

      /* --------------------------------------------------------------
       * ③ AUF-only: prune promotion keys so filters disappear
       *    immediately from the Asset Library (for both Archive + Delete)
       * ------------------------------------------------------------ */
      if (isAUF) {
        if (!organizationId) {
          console.warn(
            '[HoveredField] handleDelete(AUF): organizationId missing – ' +
              'skipping org sync.'
          );
        } else if (!deletedUfi) {
          console.log(
            '[HoveredField] handleDelete(AUF): No UFI found for field → ' +
              'nothing to prune in org promotion keys.'
          );
        } else {
          try {
            const orgRef  = doc(db, 'organizations', organizationId);
            const orgSnap = await getDoc(orgRef);

            if (!orgSnap.exists()) {
              console.warn(
                '[HoveredField] handleDelete(AUF): organizations doc not ' +
                  'found, skipping org sync.'
              );
            } else {
              const org = orgSnap.data() || {};
              const cfg = org.assetPromotionConfig || {};
              const before = {
                keys     : Array.isArray(cfg.keys)
                  ? cfg.keys.slice()
                  : [],
                arrayKeys: Array.isArray(cfg.arrayKeys)
                  ? cfg.arrayKeys.slice()
                  : [],
                dateKeys : Array.isArray(cfg.dateKeys)
                  ? cfg.dateKeys.slice()
                  : [],
              };

              const prune = (arr) =>
                Array.isArray(arr) ? arr.filter((k) => k !== deletedUfi) : [];

              const nextCfg = {
                keys     : prune(before.keys),
                arrayKeys: prune(before.arrayKeys),
                dateKeys : prune(before.dateKeys),
              };

              const changed =
                before.keys.length      !== nextCfg.keys.length ||
                before.arrayKeys.length !== nextCfg.arrayKeys.length ||
                before.dateKeys.length  !== nextCfg.dateKeys.length;

              console.log(
                '[HoveredField] handleDelete(AUF): pruning promotion keys',
                {
                  deletedUfi,
                  before,
                  after: nextCfg,
                  changed,
                }
              );

              if (changed) {
                await updateDoc(orgRef, {
                  assetPromotionConfig: nextCfg,
                });
                console.log(
                  '[HoveredField] handleDelete(AUF): ' +
                    'assetPromotionConfig updated successfully.'
                );
              } else {
                console.log(
                  '[HoveredField] handleDelete(AUF): ' +
                    'assetPromotionConfig unchanged (UFI was not promoted).'
                );
              }
            }
          } catch (orgErr) {
            console.warn(
              '[HoveredField] handleDelete(AUF): org sync failed ' +
                '(non-fatal):',
              orgErr?.message || orgErr
            );
          }
        }

        // NOTE: We intentionally do NOT rebuild the catalog here.
        // Your publish flow writes the canonical blobs (fieldsMetadata/layoutConfig)
        // and then calls buildAssetFilterCatalogHttp, ensuring labels/choices
        // reflect the current AUF.
        console.log(
          '[HoveredField] handleDelete(AUF): Skipping catalog rebuild now; ' +
            'publish will refresh catalog blobs.'
        );
      }

      console.log('[HoveredField] AUF delete guard: completed', {
        targetFieldId,
        formId,
      });
    } catch (err) {
      console.error(
        '[HoveredField] AUF delete guard: ERROR',
        err?.message || err
      );
    } finally {
      setAufDeleteGuardBusy(false);
      setAufDeleteGuardOpen(false);
      setAufDeleteGuardPayload(null);
    }
  };


  /** Pre-arm the drop-zones **before** React-DnD evaluates the drag       *
   *  (but ignore clicks that hit the HoveredFieldMenu itself).           */
  const handleMouseDown = (e) => {
  if (e.target.closest('[data-hovered-menu]')) return;       // let menu work

  // Do NOT toggle drop zones here. Let React-DnD beginDrag/endDrag control
  // drop zone lifecycle to avoid DOM reflow before DnD locks the drag source.
  if (DND_DEBUG) {
    console.log('[HoveredField] mousedown (no pre-arm)', {
      fieldId,
      activeDragId,
      isDragging,
    });
  }

  // ENTERPRISE: do NOT call setIsHovered(false) on mousedown.
  // A state update here can re-render during native dragstart and cancel the drag.
  // Hover collapse is handled by the isDragging effect above.
};


  /* -------------------------------------------------------------------- */
  /*                      Clone the child, disable itself                  */
  /* -------------------------------------------------------------------- */
  const validChild = useMemo(() => {
    const child = Array.isArray(children)
      ? children.find(React.isValidElement)
      : children;

    if (!React.isValidElement(child)) {
      console.warn('[HoveredField] Invalid children element:', children);
      return null;
    }

    return React.cloneElement(child, {
      onClick : (e) => e.stopPropagation(),
      // NEW: preserve any height/minHeight from the child and stretch to fill
      style   : {
        ...(child.props?.style || {}),  // ← keep existing styles from field component
        pointerEvents:'none',           // neutralise control itself
        height:'100%',                  // NEW
        width:'100%',                   // NEW
      },
    });
  }, [children]);

  /* -------------------------------------------------------------------- */
  /*                        Border colour / thickness                      */
  /* -------------------------------------------------------------------- */
  const borderStyle = useMemo(() => {
    if (isSelected)  return `2px solid ${SELECT_COLOR}`;
    if (isHoverOnly) return `1px solid ${HOVER_COLOR}`;
    return '1px solid transparent';
  }, [isSelected, isHoverOnly]);

  /* -------------------------------------------------------------------- */
  /*                               Render                                  */
  /* -------------------------------------------------------------------- */
  return (
    <ErrorBoundary>
      <div
        ref={fieldRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={handleMouseDown}
        style={{
          position      :'relative',
          border        : borderStyle,
          overflow      :'visible',
          zIndex        : 12,
          pointerEvents :'auto',
          // NEW: make wrapper participate in row height & stretch with child
          display       :'flex',        // NEW
          alignItems    :'stretch',     // NEW
          height        :'100%',        // NEW
          width         :'100%',        // NEW
        }}
      >
        {/* Invisible overlay that no longer intercepts the click */}
        {!isDragging && (
          <div style={{
            position      :'absolute',
            top:0,left:0,width:'100%',height:'100%',
            zIndex:14, pointerEvents:'none',        /* 🔑 no interception */
          }}/>
        )}

        {/* Blue drag bar (grab handle) */}
        {(isHovered || isSelected || isDragging) && (
          <div
            onMouseEnter={() => setIsHovered(true)}
            style={{
              position :'absolute',
              top:0,left:0,
              minWidth :'20px',
              width    :'2vw',
              height   :'100%',
              backgroundColor: isHoverOnly ? HOVER_COLOR : SELECT_COLOR,
              display  :'flex',
              alignItems:'center',
              justifyContent:'center',
              cursor   : isDragging ? 'grabbing' : 'grab',
              zIndex   :20,
            }}
          >
            <DragIndicator style={{ color:'white', fontSize:'2vw', cursor:'inherit' }}/>
          </div>
        )}

        {/* Disabled copy of the actual field */}
        {validChild}

        {/* Hover menu – only while hovering & not dragging */}
        {!isDragging && isHovered && fieldId && (
          <HoveredFieldMenu fieldId={fieldId} onDelete={handleDelete}/>
        )}

        {/* AUF-only delete guard dialog (renders only when opened) */}
        <AufFieldDeleteGuardDialog
          open={aufDeleteGuardOpen}
          onClose={() => {
            if (aufDeleteGuardBusy) return;
            setAufDeleteGuardOpen(false);
            setAufDeleteGuardPayload(null);
          }}
          onAction={handleAufDeleteGuardAction}
          busy={aufDeleteGuardBusy}
          fieldLabel={aufDeleteGuardPayload?.fieldLabel || '—'}
          fieldType={aufDeleteGuardPayload?.fieldType || '—'}
          fieldUfi={aufDeleteGuardPayload?.fieldUfi || null}
          reasons={aufDeleteGuardPayload?.reasons || null}
          isPublished={aufDeleteGuardPayload?.isPublished ?? null}
        />

      </div>
    </ErrorBoundary>
  );
});

export default HoveredField;
